import React, { useEffect, useState, createContext, useContext } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';
interface AuthContextType {
  user: User | null;
  loading: boolean;
  signUp: (email: string, password: string, metadata: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  updateUserProfile: (updates: any) => Promise<void>;
  refreshUser: () => Promise<void>;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    // Check active session
    supabase.auth.getSession().then(({
      data: {
        session
      }
    }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });
    // Listen for auth changes
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);
  const signUp = async (email: string, password: string, metadata: any) => {
    // Ensure both camelCase and snake_case keys are sent
    // for database trigger compatibility
    const enrichedMetadata = {
      ...metadata
    };
    // If camelCase keys exist, add snake_case equivalents
    if (metadata.firstName && !metadata.first_name) {
      enrichedMetadata.first_name = metadata.firstName;
    }
    if (metadata.lastName && !metadata.last_name) {
      enrichedMetadata.last_name = metadata.lastName;
    }
    if (metadata.fullName && !metadata.full_name) {
      enrichedMetadata.full_name = metadata.fullName;
    }
    // If snake_case keys exist, add camelCase equivalents
    if (metadata.first_name && !metadata.firstName) {
      enrichedMetadata.firstName = metadata.first_name;
    }
    if (metadata.last_name && !metadata.lastName) {
      enrichedMetadata.lastName = metadata.last_name;
    }
    if (metadata.full_name && !metadata.fullName) {
      enrichedMetadata.fullName = metadata.full_name;
    }
    const {
      data,
      error
    } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: enrichedMetadata
      }
    });
    return {
      data,
      error
    };
  };
  const signIn = async (email: string, password: string) => {
    const {
      data,
      error
    } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    return {
      data,
      error
    };
  };
  const signInWithGoogle = async () => {
    const {
      error
    } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent'
        }
      }
    });
    if (error) {
      throw error;
    }
  };
  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };
  const updateUserProfile = async (updates: any) => {
    if (!user) throw new Error('No user logged in');
    const {
      data,
      error
    } = await supabase.auth.updateUser({
      data: {
        ...user.user_metadata,
        ...updates
      }
    });
    if (error) throw error;
    // Update local user state
    if (data.user) {
      setUser(data.user);
    }
  };
  const refreshUser = async () => {
    const {
      data: {
        user: refreshedUser
      }
    } = await supabase.auth.getUser();
    if (refreshedUser) {
      setUser(refreshedUser);
    }
  };
  return <AuthContext.Provider value={{
    user,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    updateUserProfile,
    refreshUser
  }} data-id="element-3423">
      {children}
    </AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}